import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import * as commonServices from '../services/website.services';
import {  SWAL_SETTINGS } from '../../../utils/Constants';
import { handleServerValidations } from '../../../utils/commonfunction';


const TABS = [
  { id: 'provider', label: 'be a babe associate (provider)' },
  { id: 'client', label: 'be a babe (client)' },
];

const DISTANCE_OPTIONS = [
  'Less than 5 miles',
  '5-10 miles',
  '10-25 miles',
  '25+ miles',
];

const REFERRAL_OPTIONS = [
  'Google',
  'Social',
  'Word of Mouth',
  'Mailer',
  'In the Wild (event, pop-up, flyer, etc.)',
  "My Doctor's Office",
  'Other',
];


const EarlyAccessForm = () => {
  const [activeTab, setActiveTab] = useState(null);
  const [categories, setCategories] = useState([]);
  const isClientTab = activeTab === 'client';

  useEffect(() => {
    commonServices.getServices()
      .then((res) => { if (res?.data) setCategories(res.data); })
      .catch(() => {});
  }, []);

  const validationSchema = useMemo(
    () => Yup.object({
      zipCode: Yup.string().trim().required('Zip code is required.'),
      name: Yup.string().trim().min(2, 'Name is required.').required('Name is required.'),
      email: Yup.string().trim().email('Invalid email').required('Email is required.'),
      referral: isClientTab
        ? Yup.string().trim().required('This field is required.')
        : Yup.string().trim(),
      referralOther: Yup.string().when('referral', {
        is: 'Other',
        then: (schema) => schema.trim().required('Please describe how you heard about us.'),
        otherwise: (schema) => schema.trim(),
      }),
    }),
    [isClientTab],
  );

  const formik = useFormik({
    initialValues: {
      zipCode: '',
      distance: DISTANCE_OPTIONS[3],
      travelMiles: 26,
      selectedServices: [], // [{ categoryId, categoryTitle, subCategoryId, subCategoryTitle }]
      referral: '',
      referralOther: '',
      name: '',
      email: '',
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      const role = isClientTab ? 'babe_user' : 'babe_associate';
      const payload = {
        name: values.name.trim(),
        email: values.email.trim(),
        role,
        zip_code: values.zipCode.trim(),
        travel_distance: values.distance,
        travel_miles: values.travelMiles,
        referral: values.referral === 'Other'
          ? values.referralOther.trim() || null
          : values.referral.trim() || null,
        selected_services: values.selectedServices,
      };
      commonServices.saveEarlyAccessInquiry(payload)
        .then((response) => {
          if (response.success) {
            Swal.fire({ icon: 'success', text: response.message, ...SWAL_SETTINGS });
            resetForm();
          } else {
            Swal.fire({ icon: 'error', text: handleServerValidations(response), ...SWAL_SETTINGS });
          }
        })
        .catch(() => {
          Swal.fire({ icon: 'error', text: 'Something went wrong. Please try again.', ...SWAL_SETTINGS });
        });
    },
  });

  /**
   * Deduplicates subcategories for display.
   * Any subcategory whose title starts with "Massage" (case-insensitive)
   * is normalized to "Massage" and only the first occurrence is kept.
   * Other duplicates by exact title are also collapsed to one.
   */
  const deduplicateSubs = (subs) => {
    const seen = new Set();
    return subs.reduce((acc, sub) => {
      const normalizedTitle = /^massage/i.test(sub.title?.trim()) ? 'Massage' : sub.title?.trim();
      if (!seen.has(normalizedTitle)) {
        seen.add(normalizedTitle);
        acc.push({ ...sub, displayTitle: normalizedTitle });
      }
      return acc;
    }, []);
  };

  const isCategoryFullySelected = (cat) => {
    const subs = deduplicateSubs(cat.subCategories || []);
    const catId = String(cat._id);
    if (!subs.length) {
      return formik.values.selectedServices.some((s) => s.categoryId === catId && !s.subCategoryId);
    }
    return subs.every((sub) =>
      formik.values.selectedServices.some(
        (s) => s.categoryId === catId && s.subCategoryId === String(sub._id),
      ),
    );
  };

  const isSubSelected = (catId, subId) =>
    formik.values.selectedServices.some(
      (s) => s.categoryId === catId && s.subCategoryId === subId,
    );

  const toggleCategory = (cat) => {
    const catId = String(cat._id);
    const subs = deduplicateSubs(cat.subCategories || []);
    const current = formik.values.selectedServices;
    if (isCategoryFullySelected(cat)) {
      formik.setFieldValue('selectedServices', current.filter((s) => s.categoryId !== catId));
      return;
    }
    if (!subs.length) {
      formik.setFieldValue('selectedServices', [
        ...current.filter((s) => s.categoryId !== catId),
        { categoryId: catId, categoryTitle: cat.title, subCategoryId: null, subCategoryTitle: null },
      ]);
      return;
    }
    const existing = new Map(current.map((s) => [`${s.categoryId}__${s.subCategoryId}`, s]));
    subs.forEach((sub) => {
      const key = `${catId}__${String(sub._id)}`;
      if (!existing.has(key)) {
        existing.set(key, { categoryId: catId, categoryTitle: cat.title, subCategoryId: String(sub._id), subCategoryTitle: sub.displayTitle });
      }
    });
    formik.setFieldValue('selectedServices', [...existing.values()]);
  };

  const toggleSubCategory = (cat, sub) => {
    const catId = String(cat._id);
    const subId = String(sub._id);
    const current = formik.values.selectedServices;
    if (isSubSelected(catId, subId)) {
      formik.setFieldValue('selectedServices', current.filter((s) => !(s.categoryId === catId && s.subCategoryId === subId)));
    } else {
      formik.setFieldValue('selectedServices', [
        ...current,
        { categoryId: catId, categoryTitle: cat.title, subCategoryId: subId, subCategoryTitle: sub.displayTitle || sub.title },
      ]);
    }
  };

  const handleDistanceChange = (miles) => {
    formik.setFieldValue('travelMiles', miles);
    if (miles < 5) formik.setFieldValue('distance', DISTANCE_OPTIONS[0]);
    else if (miles <= 10) formik.setFieldValue('distance', DISTANCE_OPTIONS[1]);
    else if (miles <= 25) formik.setFieldValue('distance', DISTANCE_OPTIONS[2]);
    else formik.setFieldValue('distance', DISTANCE_OPTIONS[3]);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    formik.setErrors({});
    formik.setTouched({});
  };

  return (
    <main>
      <section className="early-access-form section-padding">
        <div className="container">
          <div className="early-access-head">
            <span className="subheading d-flex justify-content-center">keep me in the loop</span>
            <h1 className="text-center ink">I want <span>early</span> access to:</h1>
          </div>

          <div className="early-access-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`early-access-tabs__btn${
                  activeTab === tab.id
                    ? ' is-active'
                    : activeTab !== null
                    ? ' is-inactive'
                    : ''
                }`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab !== null && (
          <div className="early-access-panel">
            <form className="early-access-form-inner" onSubmit={formik.handleSubmit}>
                <div className="early-access-field">
                  <label className="early-access-label ink">
                    {activeTab === 'provider' ? 'The location I can support is:' : 'Where I need support:'}
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    className="form-control early-access-input early-access-input--wide"
                    placeholder="zip code"
                    value={formik.values.zipCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.zipCode && formik.errors.zipCode && (
                    <span className="early-access-error">{formik.errors.zipCode}</span>
                  )}
                </div>

                {activeTab === 'provider' && (
                <div className="early-access-field early-access-field--distance">
           
                  <div className="d-flex flex-column gap-3">
                    <div className="early-access-slider-box">
                      <div className="early-access-slider">
                        <span className="early-access-slider__label">Service Distance</span>
                        <span className="early-access-slider__value">{formik.values.travelMiles} miles</span>
                      </div>
                      <input
                        type="range"
                        name="travelMiles"
                        className="early-access-range"
                        min="1"
                        max="50"
                        value={formik.values.travelMiles}
                        style={{ '--range-pct': `${((formik.values.travelMiles - 1) / 49) * 100}%` }}
                        onChange={(e) => handleDistanceChange(Number(e.target.value))}
                        aria-label="Service distance in miles"
                      />
                    </div>
                  
                  </div>
                  <select
                    name="distance"
                    className="early-access-distance-fallback"
                    value={formik.values.distance}
                    onChange={(e) => {
                      formik.handleChange(e);
                      const label = e.target.value;
                      if (label === DISTANCE_OPTIONS[0]) handleDistanceChange(4);
                      else if (label === DISTANCE_OPTIONS[1]) handleDistanceChange(8);
                      else if (label === DISTANCE_OPTIONS[2]) handleDistanceChange(20);
                      else handleDistanceChange(30);
                    }}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    {DISTANCE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                )}

                <div className="early-access-field">
                  <label className="early-access-label ink">
                    {activeTab === 'provider' ? "I\u2019m interested in providing support for:" : "I\u2019m interested in receiving support for:"}
                  </label>
                  <div className="d-flex flex-column gap-3">
            
                    <div className="early-access-services">
                      <div className="d-flex flex-column gap-3">
                        {categories.slice(0, Math.ceil(categories.length / 2)).map((cat) => (
                          <div key={cat._id} className="early-access-service-card">
                            <div className="early-access-service-card__head">
                              <div className="early-access-service-card__icon">
                                <img src={cat.image} alt="" />
                              </div>
                              <span className="early-access-service-card__title">{cat.title}</span>
                              <label className="early-access-service-card__check early-access-service-card__check--head">
                                <input
                                  type="checkbox"
                                  checked={isCategoryFullySelected(cat)}
                                  onChange={() => toggleCategory(cat)}
                                />
                                <span className="early-access-service-card__box" aria-hidden="true" />
                              </label>
                            </div>
                            {cat.subCategories && cat.subCategories.length > 0 && (
                              <ul className="early-access-service-card__subs">
                                {deduplicateSubs(cat.subCategories).map((sub) => (
                                  <li key={sub._id}>
                                    <label className="early-access-service-card__sub">
                                      <input
                                        type="checkbox"
                                        checked={isSubSelected(String(cat._id), String(sub._id))}
                                        onChange={() => toggleSubCategory(cat, sub)}
                                      />
                                      <span className="early-access-service-card__box" aria-hidden="true" />
                                      <span>{sub.displayTitle}</span>
                                    </label>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="d-flex flex-column gap-3">
                        {categories.slice(Math.ceil(categories.length / 2)).map((cat) => (
                          <div key={cat._id} className="early-access-service-card">
                            <div className="early-access-service-card__head">
                              <div className="early-access-service-card__icon">
                                <img src={cat.image} alt="" />
                              </div>
                              <span className="early-access-service-card__title">{cat.title}</span>
                              <label className="early-access-service-card__check early-access-service-card__check--head">
                                <input
                                  type="checkbox"
                                  checked={isCategoryFullySelected(cat)}
                                  onChange={() => toggleCategory(cat)}
                                />
                                <span className="early-access-service-card__box" aria-hidden="true" />
                              </label>
                            </div>
                            {cat.subCategories && cat.subCategories.length > 0 && (
                              <ul className="early-access-service-card__subs">
                                {deduplicateSubs(cat.subCategories).map((sub) => (
                                  <li key={sub._id}>
                                    <label className="early-access-service-card__sub">
                                      <input
                                        type="checkbox"
                                        checked={isSubSelected(String(cat._id), String(sub._id))}
                                        onChange={() => toggleSubCategory(cat, sub)}
                                      />
                                      <span className="early-access-service-card__box" aria-hidden="true" />
                                      <span>{sub.displayTitle}</span>
                                    </label>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="early-access-field">
                      <label className="early-access-label ink">How did you hear about us?</label>
                      <select
                        name="referral"
                        className="form-control early-access-input early-access-input--wide"
                        value={formik.values.referral}
                        onChange={(e) => {
                          formik.handleChange(e);
                          if (e.target.value !== 'Other') {
                            formik.setFieldValue('referralOther', '');
                          }
                        }}
                        onBlur={formik.handleBlur}
                      >
                        <option value="">Select an option</option>
                        {REFERRAL_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                      {formik.touched.referral && formik.errors.referral && (
                        <span className="early-access-error">{formik.errors.referral}</span>
                      )}
                    </div>
                    {formik.values.referral === 'Other' && (
                      <div className="early-access-field">
                        <input
                          type="text"
                          name="referralOther"
                          className="form-control early-access-input early-access-input--wide"
                          placeholder="please describe how you heard about us"
                          value={formik.values.referralOther}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.referralOther && formik.errors.referralOther && (
                          <span className="early-access-error">{formik.errors.referralOther}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="early-access-field-row">
                  <div className="early-access-field">
                    <label className="early-access-label ink">Full name</label>
                    <input
                      type="text"
                      name="name"
                      className="form-control early-access-input"
                      placeholder="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <span className="early-access-error">{formik.errors.name}</span>
                    )}
                  </div>
                  <div className="early-access-field">
                    <label className="early-access-label ink">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control early-access-input"
                      placeholder="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <span className="early-access-error">{formik.errors.email}</span>
                    )}
                  </div>
                </div>

                <div className="early-access-submit">
                  <button type="submit" className="btn btn-purple">submit</button>
                </div>
              </form>
          </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default EarlyAccessForm;
